import { isUuidV7 } from '@nest/ids';
import { t } from '@/lib/i18n';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  PageContainer,
  ProCard,
  ProDescriptions,
} from '@ant-design/pro-components';
import { message, Button, Popconfirm, Result, Space, Tag, Tooltip } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { AsyncBoundary } from '@/components/async-boundary';
import { canAccess, useAccess } from '@/features/auth/access';
import {
  useDeactivateUser,
  useOwnUser,
  useRestoreUser,
  useUser,
  useUserDepartmentOptions,
} from '@/features/user/user-api';
import { PhotoManager } from '@/features/user/photo/manager';
import { UserCredentialsActions } from '@/features/user/credentials-modal';
import { UserSessionsCard } from '@/features/user/session/list';
import {
  displayName,
  genderLabels,
  roleLabels,
} from '@/features/user/user-types';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { formatDateTime, fromNow } from '@/lib/format';
import { describeError } from '@/lib/http/http-api';

/**
 * One user, with its photos.
 *
 * `GET /users/:id` is admin-or-self on the API, so this page is reachable by an admin for
 * anyone and by a signed-in user for themselves only. A 404 and a 403 are both rendered as
 * "not available" rather than as an error, because neither is an error in the app.
 */
export const Route = createFileRoute('/_authenticated/system/users/$userId')({
  component: UserDetailPage,
});

function UserDetailPage() {
  const { userId } = Route.useParams();
  const access = useAccess();
  const navigate = useNavigate();
  const isValidId = isUuidV7(userId);

  const user = useUser(userId, { enabled: isValidId });
  const deactivate = useDeactivateUser();
  const restore = useRestoreUser();
  const canReadUsers = canAccess(access.data, 'users:read');
  const departments = useUserDepartmentOptions(canReadUsers && isValidId);
  const canWriteUsers = canAccess(access.data, 'users:write');
  const own = useOwnUser({
    enabled: isValidId && access.isSuccess && !canWriteUsers,
  });
  const writable =
    canWriteUsers &&
    (access.data?.isAdmin === true || user.data?.role !== 'admin');

  useDocumentTitle(isValidId ? t('用户 #{{id}}', { id: userId }) : t('用户'));

  if (!isValidId) {
    return (
      <Result
        status="404"
        title={t('用户不存在')}
        subTitle={t('“{{id}}”不是一个合法的用户编号。', { id: userId })}
        extra={
          <Link to="/system/users">
            <Button type="primary">{t('返回用户列表')}</Button>
          </Link>
        }
      />
    );
  }

  return (
    <PageContainer
      header={{
        title: user.data ? displayName(user.data) : t('用户'),
        onBack: () => void navigate({ to: '/system/users' }),
        extra: [
          <Link key="list" to="/system/users">
            <Button icon={<ArrowLeftOutlined />}>{t('返回列表')}</Button>
          </Link>,
        ],
      }}
    >
      <AsyncBoundary result={user} skeletonRows={6}>
        {(data) => (
          <Space orientation="vertical" size={16} className="flex">
            <ProCard title={t('基本信息')} variant="outlined">
              <ProDescriptions
                column={2}
                dataSource={data}
                columns={[
                  {
                    title: t('编号'),
                    dataIndex: 'id',
                    copyable: true,
                    className: 'break-all',
                  },
                  {
                    title: t('姓名'),
                    dataIndex: 'firstName',
                    render: (_, record) => displayName(record),
                  },
                  { title: t('邮箱'), dataIndex: 'email', copyable: true },
                  ...(canReadUsers
                    ? [
                        {
                          title: t('所属部门'),
                          dataIndex: 'departmentId',
                          render: () =>
                            data.departmentId === null
                              ? t('未分配')
                              : (departments.data?.find(
                                  (department) =>
                                    department.id === data.departmentId,
                                )?.name ??
                                t('部门 #{{id}}', { id: data.departmentId })),
                        },
                      ]
                    : []),
                  {
                    title: t('手机号'),
                    dataIndex: 'phone',
                    copyable: true,
                    render: (_, record) => record.phone ?? t('未填写'),
                  },
                  {
                    title: t('性别'),
                    dataIndex: 'gender',
                    render: (_, record) =>
                      record.gender === null
                        ? t('未填写')
                        : genderLabels[record.gender],
                  },
                  {
                    title: t('生日'),
                    dataIndex: 'birthday',
                    // The API answers with the calendar day itself, so there is nothing to
                    // format and no timezone to be wrong about.
                    render: (_, record) => record.birthday ?? t('未填写'),
                  },
                  {
                    title: t('账号身份'),
                    dataIndex: 'role',
                    render: (_, record) =>
                      record.role === 'admin' ? (
                        <Tag color="gold">{roleLabels.admin}</Tag>
                      ) : (
                        <Tag>{roleLabels.user}</Tag>
                      ),
                  },
                  {
                    title: t('状态'),
                    dataIndex: 'isActive',
                    render: (_, record) =>
                      record.isActive ? (
                        <Tag color="success">{t('启用')}</Tag>
                      ) : (
                        <Tag color="default">{t('已停用')}</Tag>
                      ),
                  },
                  {
                    title: t('创建时间'),
                    dataIndex: 'createdAt',
                    render: (_, record) => (
                      <Tooltip title={formatDateTime(record.createdAt)}>
                        <span>{fromNow(record.createdAt)}</span>
                      </Tooltip>
                    ),
                  },
                  {
                    title: t('照片'),
                    dataIndex: 'photos',
                    span: 'filled',
                    render: (_, record) =>
                      t('{{count}} 张', { count: record.photos.length }),
                  },
                  ...(canReadUsers
                    ? [
                        {
                          title: t('备注'),
                          dataIndex: 'remark',
                          // An operator's note can be a sentence; it gets the whole width.
                          span: 2,
                          render: () => data.remark ?? '—',
                        },
                      ]
                    : []),
                ]}
              />
            </ProCard>

            {access.data?.isAdmin === true && writable && data.isActive ? (
              <ProCard title={t('登录凭据')} variant="outlined">
                <UserCredentialsActions user={data} />
              </ProCard>
            ) : null}

            {access.data?.isAdmin === true ? (
              <UserSessionsCard userId={data.id} writable={writable} />
            ) : null}

            <ProCard title={t('照片')} variant="outlined">
              <PhotoManager
                userId={data.id}
                photos={data.photos}
                canUpload={writable || own.data?.id === data.id}
              />
            </ProCard>

            {writable ? (
              <ProCard title={t('账号状态')} variant="outlined">
                <Space orientation="vertical" size={8}>
                  <Popconfirm
                    title={
                      data.isActive
                        ? t('停用这个用户？')
                        : t('重新启用这个用户？')
                    }
                    description={
                      data.isActive
                        ? t(
                            '仅停用当前租户档案；资料和照片保留，不影响其他租户。',
                          )
                        : t('恢复当前租户访问，不影响其他租户或账号封禁。')
                    }
                    okText={data.isActive ? t('停用') : t('启用')}
                    cancelText={t('取消')}
                    okButtonProps={{ danger: data.isActive }}
                    onConfirm={async () => {
                      try {
                        if (data.isActive)
                          await deactivate.mutateAsync(data.id);
                        else await restore.mutateAsync(data.id);
                        message.success(
                          data.isActive ? t('用户已停用') : t('用户已启用'),
                        );
                      } catch (error) {
                        message.error(describeError(error));
                        throw error;
                      }
                    }}
                  >
                    <Button
                      danger={data.isActive}
                      loading={deactivate.isPending || restore.isPending}
                    >
                      {data.isActive ? t('停用用户') : t('重新启用用户')}
                    </Button>
                  </Popconfirm>
                </Space>
              </ProCard>
            ) : null}
          </Space>
        )}
      </AsyncBoundary>
    </PageContainer>
  );
}
